import React, { useState, useEffect } from 'react';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Card } from '../../ui/card';

interface FormData {
  C184_AssessmentId: string;
  C184_Instructions: string;
}

export function EditAssessmentInstructions({ assessmentId }: { assessmentId: string }) {
  const [getFormData] = useLazyQuery("C185v1_GetC184FormData");
  const [submitCommand] = useLazyCommand("C184v1_EditAssessmentInstructions");
  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (assessmentId) {
      getFormData({
        C185_AssessmentId: assessmentId
      });
    }
  }, [assessmentId]);

  useEvent({
    eventName: "E152v1_GotC184FormData",
    onEventReceived: (event: any) => {
      // The event contains e000_FieldData with flattened field structure (camelCase in browser)
      const fieldData = event.e000_FieldData || [];
      
      // Find the C184 fields
      const assessmentIdField = fieldData.find((field: any) => field.name === 'c184_AssessmentId');
      const instructionsField = fieldData.find((field: any) => field.name === 'c184_Instructions');
      
      setFormData({
        C184_AssessmentId: assessmentIdField?.initialValue || assessmentId,
        C184_Instructions: instructionsField?.initialValue || ''
      });
      setLoading(false);
    }
  }, []);

  useEvent({
    eventName: "E149v1_AssessmentUpdated",
    onEventReceived: () => {
      setSaving(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData) {
      return;
    }

    setSaving(true);
    submitCommand({
      $type: "C184v1_EditAssessmentInstructions",
      C184_AssessmentId: formData.C184_AssessmentId,
      C184_Instructions: formData.C184_Instructions
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-blue"></div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="instructions" className="text-sm font-medium">
            Instructions
          </Label>
          <p className="text-sm text-gray-600 mb-2">
            Provide instructions that will be shown to students before they begin the assessment.
          </p>
          <textarea
            id="instructions"
            value={formData.C184_Instructions}
            onChange={(e) => setFormData({ ...formData, C184_Instructions: e.target.value })}
            placeholder="Enter assessment instructions..."
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue font-mono text-sm"
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

